import { LocationType } from '@/entities/location';
import { defineStore } from 'pinia';
import { ref } from 'vue';

type LocationKey = 'city' | 'region' | 'country';
const PRELOAD_DATA_KEY = '__PRELOAD_experience-mf-listing__' as const;

const LOCATION_KEY_MAP: Record<LocationType, LocationKey> = {
  [LocationType.CITY]: 'city',
  [LocationType.REGION]: 'region',
  [LocationType.COUNTRY]: 'country',
} as const;

interface SkeletonPrefetchData {
  tags?: Record<string, string[]>;
  exp_selections_count?: Record<string, number>;
  exp_count_by_tag?: Record<string, number>;
}

export const useSkeletonPrefetchStore = defineStore('skeletonPrefetch', () => {
  const data = ref(window[PRELOAD_DATA_KEY]);

  const getPrefetchData = () => {
    if (!data.value || typeof data.value !== 'object') {
      return null;
    }

    if (!('tags' in data.value) && !('exp_selections_count' in data.value) && !('exp_count_by_tag' in data.value)) {
      return null;
    }

    return data.value as SkeletonPrefetchData;
  };

  const getLocationKey = (location: LocationType) => {
    return LOCATION_KEY_MAP[location];
  };

  const getTagsSkeleton = (location: LocationType) => {
    const locationKey = getLocationKey(location);

    return getPrefetchData()?.tags?.[locationKey] ?? null;
  };

  const getExpSelectionSkeleton = (location: LocationType) => {
    const locationKey = getLocationKey(location);

    return getPrefetchData()?.exp_selections_count?.[locationKey] ?? null;
  };

  const getExpCountByTagSkeleton = () => {
    return getPrefetchData()?.exp_count_by_tag ?? null;
  };

  const clearPrefetchData = () => {
    delete window[PRELOAD_DATA_KEY];
    data.value = null;
  };

  return { getTagsSkeleton, getExpSelectionSkeleton, getExpCountByTagSkeleton, clearPrefetchData };
});
